/**
 * Iconos de la taxonomía comercial cd-2b.
 *
 * El catálogo (`api/src/data/cd2bCatalogo.js`) manda el nombre del icono en `icono`, en
 * PascalCase de lucide — la misma convención que ya usa el sitio de marketing
 * (`portal-nextjs/src/components/cd2b/icon-for.tsx`). Aquí solo se resuelve el nombre a
 * componente, con dos respaldos en cascada: el icono de la FAMILIA y, si todo falla, uno
 * genérico. Un nombre inválido nunca debe romper la pantalla del asesor.
 *
 * Se importan por nombre (no `import * as lucide`) para que el bundle no se lleve las ~1500
 * siluetas de la librería.
 */
import {
  Boxes, ShoppingCart, CalendarDays, Calculator, Users, Factory, Building2, Layers,
  Banknote, Truck, MapPin, UserPlus, UserRound, Store, PackagePlus, FilePlus2, LifeBuoy,
  Star, Rocket, ArrowRightLeft, Brush, ArrowDownUp, Upload, GraduationCap, Palette, Plug,
  ShieldCheck, FileText, Clock, Wrench, Gauge, Briefcase, Puzzle, Package, Layout, TicketPercent,
  Contact, MessageCircle, Megaphone, Bot, Route, BadgePercent, SlidersHorizontal, Building, BookText, Inbox,
  Archive, ArchiveRestore,
  Smartphone, BedDouble, UtensilsCrossed, Radio, Repeat, Bus, MonitorPlay,
} from 'lucide-react'
import ICONO_GLIFOS from './cd2bIconosGlifos.generated'

/**
 * El respaldo en cascada es correcto para un nombre INVÁLIDO, pero también tapaba un nombre
 * VÁLIDO que simplemente no se había importado aquí: la fila salía con el icono de su familia,
 * sin error ni aviso, y en una pantalla llena de filas nadie nota que tres llevan el genérico.
 * Pasó con `Radio` (Sensores y telemetría), `Repeat` (Cobro Recurrente) y `Bus` (Logística
 * Escolar). Lo vigila `api/tests/unit/cd2bIconosSuperficies.test.js`, que barre este fuente y
 * lo cruza contra el catálogo — derivado, no una lista paralela que mantener.
 */
const ICONOS = {
  Boxes, ShoppingCart, CalendarDays, Calculator, Users, Factory, Building2, Layers,
  Banknote, Truck, MapPin, UserPlus, UserRound, Store, PackagePlus, FilePlus2, LifeBuoy,
  Star, Rocket, ArrowRightLeft, Brush, ArrowDownUp, Upload, GraduationCap, Palette, Plug,
  ShieldCheck, FileText, Clock, Wrench, Gauge, Briefcase, Puzzle, Package, Layout, TicketPercent,
  Contact, MessageCircle, Megaphone, Bot, Route, BadgePercent, SlidersHorizontal, Building, BookText, Inbox,
  Archive, ArchiveRestore,
  Smartphone, BedDouble, UtensilsCrossed, Radio, Repeat, Bus, MonitorPlay,
}

/**
 * Familias. `plan` y `descuento` no son familias del catálogo — son líneas de presentación
 * del desglose — pero necesitan icono y etiqueta igual, así que viven aquí junto al resto.
 */
export const FAMILIA = {
  plan: { label: 'Plan', plural: 'Plan contratado', icono: 'Layout', color: 'text-primary-700 dark:text-primary-300' },
  modulo: { label: 'Módulo', plural: 'Módulos del sistema', icono: 'Boxes', color: 'text-primary-700 dark:text-primary-300' },
  capacidad: { label: 'Cupo', plural: 'Ampliaciones de cupo', icono: 'Gauge', color: 'text-warning-700 dark:text-warning-300' },
  asiento: { label: 'Usuario', plural: 'Usuarios adicionales', icono: 'UserPlus', color: 'text-info-700 dark:text-info-300' },
  soporte: { label: 'Soporte', plural: 'Soporte', icono: 'LifeBuoy', color: 'text-primary-700 dark:text-primary-300' },
  servicio: { label: 'Servicio', plural: 'Servicios profesionales', icono: 'Briefcase', color: 'text-neutral-600 dark:text-neutral-400' },
  plugin: { label: 'Plugin', plural: 'Plugins', icono: 'Puzzle', color: 'text-success-700 dark:text-success-300' },
  descuento: { label: 'Descuento', plural: 'Descuentos', icono: 'TicketPercent', color: 'text-success-700 dark:text-success-300' },
}

/** Orden en que se presentan las familias (de lo estructural a lo accesorio). */
export const ORDEN_FAMILIA = ['plan', 'modulo', 'capacidad', 'asiento', 'soporte', 'plugin', 'servicio', 'descuento']

/** Resuelve un nombre de icono a componente. `null` si no hay nada que resolver. */
export function componenteIcono(nombre) {
  return (nombre && ICONOS[nombre]) || null
}

/**
 * Icono de un ítem del catálogo. Prioridad: el suyo → el de su familia → genérico.
 *
 * @param {{icono?:string, tipo?:string}} item
 */
export function IconoItem({ item, size = 15, className = '' }) {
  const fam = FAMILIA[item?.tipo]
  const Cmp = componenteIcono(item?.icono) || componenteIcono(fam?.icono) || Package
  return <Cmp size={size} className={`${fam?.color || 'text-neutral-500 dark:text-neutral-400'} ${className}`.trim()} aria-hidden="true" />
}

/** Icono de una FAMILIA (encabezados de sección, chips, leyendas). */
export function IconoFamilia({ tipo, size = 15, className = '' }) {
  const fam = FAMILIA[tipo]
  const Cmp = componenteIcono(fam?.icono) || Package
  return <Cmp size={size} className={`${fam?.color || 'text-neutral-500 dark:text-neutral-400'} ${className}`.trim()} aria-hidden="true" />
}

/** Etiqueta legible de una familia (con respaldo al propio código si es desconocida). */
export function labelFamilia(tipo, { plural = false } = {}) {
  const fam = FAMILIA[tipo]
  if (!fam) return tipo || ''
  return plural ? fam.plural : fam.label
}

/**
 * Agrupa las líneas del desglose por familia, en el orden de `ORDEN_FAMILIA`, con subtotal.
 * Las familias que no aparecen se omiten; una familia desconocida se agrupa igual, al final,
 * en vez de desaparecer de la cotización.
 */
export function agruparDesglose(desglose = []) {
  const porTipo = new Map()
  for (const l of desglose) {
    const t = l.tipo || 'modulo'
    if (!porTipo.has(t)) porTipo.set(t, [])
    porTipo.get(t).push(l)
  }
  const orden = [...ORDEN_FAMILIA, ...[...porTipo.keys()].filter((t) => !ORDEN_FAMILIA.includes(t))]
  return orden
    .filter((t) => porTipo.has(t))
    .map((tipo) => {
      const lineas = porTipo.get(tipo)
      return { tipo, label: labelFamilia(tipo, { plural: true }), lineas, subtotal: lineas.reduce((s, l) => s + (Number(l.precio) || 0), 0) }
    })
}

/**
 * Glifos SVG como STRING, para el documento imprimible autocontenido que genera
 * `PreviewCotizacion.documentoHTML` (se abre en otra ventana y no tiene React ni Tailwind).
 * Espejan `api/src/services/cd2bDesgloseVista.js`, que es el que produce el MISMO documento
 * para el correo y la página pública de aceptación: si divergen, el asesor imprimiría una
 * cotización distinta de la que recibe el cliente.
 */
const GLIFO = {
  plan: '<rect x="3" y="4" width="18" height="16" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>',
  modulo: '<path d="M12 2 21 7v10l-9 5-9-5V7z"/><path d="M3 7l9 5 9-5"/><line x1="12" y1="12" x2="12" y2="22"/>',
  capacidad: '<path d="M4 18a8 8 0 1 1 16 0"/><line x1="12" y1="18" x2="16" y2="12"/>',
  asiento: '<circle cx="12" cy="8" r="3.5"/><path d="M5 20a7 7 0 0 1 14 0"/>',
  soporte: '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.5"/>',
  servicio: '<rect x="3" y="7" width="18" height="13" rx="2"/><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>',
  plugin: '<line x1="9" y1="2" x2="9" y2="8"/><line x1="15" y1="2" x2="15" y2="8"/><path d="M6 8h12v4a6 6 0 0 1-12 0z"/><line x1="12" y1="18" x2="12" y2="22"/>',
  descuento: '<path d="M20 12 12 20 3 11V4h7z"/><circle cx="8" cy="8" r="1.5"/>',
}

const COLOR_DOC = {
  plan: '#4338ca', modulo: '#4338ca', capacidad: '#b45309', asiento: '#0369a1',
  soporte: '#6d28d9', plugin: '#047857', servicio: '#525252', descuento: '#15803d',
}

/** Etiquetas del documento del cliente (más explícitas que las del editor). */
const LABEL_DOC = {
  plan: 'Plan contratado', modulo: 'Módulos del sistema', capacidad: 'Ampliaciones de cupo',
  asiento: 'Usuarios adicionales', soporte: 'Soporte', plugin: 'Plugins',
  servicio: 'Servicios profesionales', descuento: 'Descuentos',
}

/** `<svg>` como string para incrustar en el HTML imprimible. */
export function svgFamiliaHTML(tipo, size = 13) {
  const g = GLIFO[tipo] || GLIFO.modulo
  const color = COLOR_DOC[tipo] || '#525252'
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" `
    + `stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle">${g}</svg>`
}

/**
 * `<svg>` del icono PROPIO de una línea del desglose (`l.icono`, ver
 * `cd2bPricingService.js:linea()`), con el glifo de familia como respaldo. Espeja
 * `svgLinea()` de `api/src/services/cd2bDesgloseVista.js` — mismo dato, mismo generado
 * (`cd2bIconosGlifos.generated.js`), para que el PDF impreso por el asesor no diverja del
 * correo/página pública que genera el backend con los mismos datos.
 */
export function svgLineaHTML(l, size = 13) {
  const tipo = l?.tipo || 'modulo'
  const g = (l?.icono && ICONO_GLIFOS[l.icono]) || GLIFO[tipo] || GLIFO.modulo
  const color = COLOR_DOC[tipo] || '#525252'
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" `
    + `stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle">${g}</svg>`
}

/** Etiqueta de familia tal como se rotula en el documento del cliente. */
export function labelFamiliaDoc(tipo) {
  return LABEL_DOC[tipo] || labelFamilia(tipo, { plural: true })
}
